import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import './SetupAccount.css';

function SetupAccount() {
  const navigate = useNavigate();
  const { currentUser, userProfile, fetchAndSetProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Get localStorage key based on user ID
  const getStorageKey = (field) => `setupAccount_${currentUser?.id || 'anonymous'}_${field}`;

  // Load from localStorage or use empty string
  const loadFromStorage = (field, defaultValue = '') => {
    if (!currentUser?.id) return defaultValue;
    try {
      const stored = localStorage.getItem(getStorageKey(field));
      return stored !== null ? stored : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Save to localStorage
  const saveToStorage = (field, value) => {
    if (!currentUser?.id) return;
    try {
      if (value) {
        localStorage.setItem(getStorageKey(field), value);
      } else {
        localStorage.removeItem(getStorageKey(field));
      }
    } catch (e) {
      console.warn('[SetupAccount] Failed to save to localStorage:', e);
    }
  };

  const [displayName, setDisplayName] = useState(() => loadFromStorage('displayName'));
  const [username, setUsername] = useState(() => loadFromStorage('username'));
  const [gender, setGender] = useState(() => loadFromStorage('gender'));
  const [phoneCountryCode, setPhoneCountryCode] = useState(() => loadFromStorage('phoneCountryCode', '+971'));
  const [phoneNumber, setPhoneNumber] = useState(() => loadFromStorage('phoneNumber'));
  const [age, setAge] = useState(() => loadFromStorage('age'));
  const [referralCode, setReferralCode] = useState(() => loadFromStorage('referralCode'));

  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);

  const fileInputRef = useRef(null);
  const hasLoadedFromDB = useRef(false);

  // Load profile from database only once on mount
  useEffect(() => {
    if (!currentUser || hasLoadedFromDB.current) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('display_name, username, avatar_url, age, gender, phone_country_code, phone_number')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        // Check if user already has a referral code
        const { data: existingReferralCode } = await supabase
          .from('user_referral_codes')
          .select('code')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (profileError) {
          console.error('[SetupAccount] load profile error:', profileError);
        }

        if (cancelled) return;

        // Check localStorage first, then DB
        const storedDisplayName = loadFromStorage('displayName');
        const storedUsername = loadFromStorage('username');
        const storedGender = loadFromStorage('gender');
        const storedPhoneNumber = loadFromStorage('phoneNumber');
        const storedPhoneCountryCode = loadFromStorage('phoneCountryCode');
        const storedAge = loadFromStorage('age');
        const storedReferralCode = loadFromStorage('referralCode');

        // Only set from DB if localStorage is empty
        if (!storedDisplayName) {
          const baseName =
            profile?.display_name ||
            userProfile?.display_name ||
            (currentUser.email ? currentUser.email.split('@')[0] : '');
          if (baseName) {
            setDisplayName(baseName);
            saveToStorage('displayName', baseName);
          }
        } else {
          setDisplayName(storedDisplayName);
        }

        if (!storedUsername && (profile?.username || userProfile?.username)) {
          const user = profile?.username || userProfile?.username || '';
          setUsername(user);
          saveToStorage('username', user);
        } else if (storedUsername) {
          setUsername(storedUsername);
        }

        // Load from direct columns, not stats JSONB
        if (!storedGender && profile?.gender) {
          setGender(profile.gender);
          saveToStorage('gender', profile.gender);
        } else if (storedGender) {
          setGender(storedGender);
        }
        
        if (!storedPhoneNumber && profile?.phone_number) {
          setPhoneNumber(profile.phone_number);
          saveToStorage('phoneNumber', profile.phone_number);
        } else if (storedPhoneNumber) {
          setPhoneNumber(storedPhoneNumber);
        }
        
        if (!storedPhoneCountryCode && profile?.phone_country_code) {
          setPhoneCountryCode(profile.phone_country_code);
          saveToStorage('phoneCountryCode', profile.phone_country_code);
        } else if (storedPhoneCountryCode) {
          setPhoneCountryCode(storedPhoneCountryCode);
        } else if (!storedPhoneCountryCode && !profile?.phone_country_code) {
          // Default to +971 if nothing exists
          setPhoneCountryCode('+971');
          saveToStorage('phoneCountryCode', '+971');
        }
        
        if (!storedAge && profile?.age) {
          setAge(String(profile.age));
          saveToStorage('age', String(profile.age));
        } else if (storedAge) {
          setAge(storedAge);
        }
        
        // Load referral code from user_referral_codes table
        if (!storedReferralCode && existingReferralCode?.code) {
          setReferralCode(existingReferralCode.code);
          saveToStorage('referralCode', existingReferralCode.code);
        } else if (storedReferralCode) {
          setReferralCode(storedReferralCode);
        }

        // Load avatar preview from existing avatar_url
        const storedPreview = localStorage.getItem(getStorageKey('avatarPreview'));
        if (storedPreview) {
          setAvatarPreview(storedPreview);
        } else if (profile?.avatar_url) {
          try {
            const { data } = supabase.storage
              .from('avatars')
              .getPublicUrl(`${currentUser.id}/${profile.avatar_url}`);
            if (data?.publicUrl) {
              setAvatarPreview(data.publicUrl);
              try {
                localStorage.setItem(getStorageKey('avatarPreview'), data.publicUrl);
              } catch {}
            }
          } catch (e) {
            console.warn('[SetupAccount] error resolving avatar url', e);
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[SetupAccount] unexpected load error:', e);
          setError(e.message || 'Failed to load profile');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          hasLoadedFromDB.current = true;
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const preview = reader.result;
      setAvatarPreview(preview);
      // Save preview to localStorage for persistence
      try {
        localStorage.setItem(getStorageKey('avatarPreview'), preview);
      } catch (e) {
        console.warn('[SetupAccount] Failed to save avatar preview to localStorage:', e);
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatarIfNeeded = async () => {
    if (!avatarFile || !currentUser?.id) return null;

    // Format: avatar_<timestamp>.jpg
    const timestamp = Date.now();
    const filename = `avatar_${timestamp}.jpg`;
    const folder = `${currentUser.id}`;
    const fullPath = `${folder}/${filename}`;

    // Convert image to JPG format if needed
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    return new Promise((resolve, reject) => {
      img.onload = async () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(async (blob) => {
          if (!blob) {
            reject(new Error('Failed to convert image to blob'));
            return;
          }
          
          const { error } = await supabase.storage.from('avatars').upload(fullPath, blob, {
            upsert: true,
            contentType: 'image/jpeg',
          });

          if (error) {
            console.error('[SetupAccount] avatar upload error:', error);
            reject(error);
            return;
          }

          // Clear localStorage avatar preview after successful upload
          try {
            localStorage.removeItem(getStorageKey('avatarPreview'));
          } catch {}
          
          // We store only the filename; AuthContext knows how to resolve it.
          resolve(filename);
        }, 'image/jpeg', 0.9);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(avatarFile);
    });
  };

  // Sync state to localStorage whenever it changes
  useEffect(() => {
    if (displayName) saveToStorage('displayName', displayName);
  }, [displayName]);

  useEffect(() => {
    if (username) saveToStorage('username', username);
  }, [username]);

  useEffect(() => {
    if (gender) saveToStorage('gender', gender);
  }, [gender]);

  useEffect(() => {
    if (phoneCountryCode) saveToStorage('phoneCountryCode', phoneCountryCode);
  }, [phoneCountryCode]);

  useEffect(() => {
    if (phoneNumber) saveToStorage('phoneNumber', phoneNumber);
  }, [phoneNumber]);

  useEffect(() => {
    if (age) saveToStorage('age', age);
  }, [age]);

  useEffect(() => {
    if (referralCode) saveToStorage('referralCode', referralCode);
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.id || saving) return;

    if (!displayName.trim() || !username.trim()) {
      setError('Display name and username are required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const avatarFilename = await uploadAvatarIfNeeded();

      // Build update payload with direct columns, not stats JSONB
      const updatePayload = {
        display_name: displayName.trim(),
        username: username.trim(),
        gender: gender || null,
        phone_country_code: phoneCountryCode || null,
        phone_number: phoneNumber || null,
        age: age ? parseInt(age, 10) : null,
      };

      if (avatarFilename) {
        updatePayload.avatar_url = avatarFilename;
      }

      // Get existing stats to preserve other fields, but remove the ones we're moving to direct columns
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('stats')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      // Clean up stats JSONB - remove fields that should be in direct columns
      let cleanedStats = existingProfile?.stats || {};
      if (typeof cleanedStats === 'object' && cleanedStats !== null) {
        // Remove these fields from stats as they're now in direct columns
        const { age, gender, phone_number, phone_country_code, referral_code, is_new_user, setup_completed, ...rest } = cleanedStats;
        cleanedStats = rest;
      }

      // Update profile with direct columns AND cleaned stats
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          ...updatePayload,
          stats: cleanedStats, // Save cleaned stats without the moved fields
        })
        .eq('user_id', currentUser.id);

      if (updateError) {
        console.error('[SetupAccount] profile update error:', updateError);
        throw updateError;
      }

      // Handle referral code - create or update record in user_referral_codes if provided
      if (referralCode && referralCode.trim()) {
        // Get referral validity hours from app_settings (default 3 hours)
        const { data: appSettings } = await supabase
          .from('app_settings')
          .select('referral_link_validity_hours, referral_participants_required')
          .maybeSingle();

        const validityHours = appSettings?.referral_link_validity_hours || 3.0;
        const participantsRequired = appSettings?.referral_participants_required || 3;
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + validityHours);

        // Gold reward ID from user_referral_rewards table
        const goldRewardId = 'f658207f-6682-4f76-acb3-29a809ac7292';

        // Check if user already has a referral code
        const { data: existingCode } = await supabase
          .from('user_referral_codes')
          .select('id')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (existingCode) {
          // Update existing referral code with new code and reward_id
          const { error: updateError } = await supabase
            .from('user_referral_codes')
            .update({
              code: referralCode.trim(),
              reward_id: goldRewardId,
              participants_required: participantsRequired,
              expires_at: expiresAt.toISOString(),
            })
            .eq('user_id', currentUser.id);

          if (updateError) {
            console.error('[SetupAccount] referral code update error:', updateError);
            // Don't throw - referral code is optional, continue with profile save
          }
        } else {
          // Create new referral code record with reward_id
          const { error: referralError } = await supabase
            .from('user_referral_codes')
            .insert({
              user_id: currentUser.id,
              code: referralCode.trim(),
              reward_id: goldRewardId,
              participants_required: participantsRequired,
              current_participants_count: 0,
              status: 'active',
              expires_at: expiresAt.toISOString(),
            });

          if (referralError) {
            console.error('[SetupAccount] referral code creation error:', referralError);
            // Don't throw - referral code is optional, continue with profile save
          }
        }
      }

      // Clear all localStorage keys for this user after successful save
      try {
        const keysToClear = [
          'displayName',
          'username',
          'gender',
          'phoneCountryCode',
          'phoneNumber',
          'age',
          'referralCode',
          'avatarPreview',
        ];
        keysToClear.forEach((key) => localStorage.removeItem(getStorageKey(key)));
      } catch {}

      // Refresh auth context profile and go to feed
      await fetchAndSetProfile({
        id: currentUser.id,
        email: currentUser.email,
        user_metadata: currentUser.user_metadata || {},
      });

      navigate('/feed', { replace: true });
    } catch (err) {
      console.error('[SetupAccount] submit error:', err);
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="setup-page-container">
        <div className="setup-card">
          <p className="setup-loading-text">Please sign in to set up your account.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="setup-page-container">
        <div className="setup-card">
          <p className="setup-loading-text">Loading your profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-page-container">
      <div className="setup-card">
        <h1 className="setup-title">Complete Your Profile</h1>
        <p className="setup-subtitle">Customize how you appear on iWinto before you start exploring the feed.</p>

        <div className="setup-avatar-section">
          <button type="button" className="setup-avatar-circle" onClick={handleAvatarClick}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar preview" />
            ) : (
              <span className="setup-avatar-placeholder">Add photo</span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
          <p className="setup-avatar-hint">Tap to add profile photo</p>
        </div>

        <form className="setup-form" onSubmit={handleSubmit}>
          <div className="setup-two-column">
            <div className="setup-field">
              <label>Display name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>
            <div className="setup-field">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="@username"
                required
              />
            </div>
          </div>

          <div className="setup-field">
            <label>
              Gender <span className="setup-optional">(Optional)</span>
            </label>
            <div className="setup-gender-row">
              <button
                type="button"
                className={`setup-gender-pill ${gender === 'male' ? 'active' : ''}`}
                onClick={() => setGender(gender === 'male' ? '' : 'male')}
              >
                Male
              </button>
              <button
                type="button"
                className={`setup-gender-pill ${gender === 'female' ? 'active' : ''}`}
                onClick={() => setGender(gender === 'female' ? '' : 'female')}
              >
                Female
              </button>
            </div>
          </div>

          <div className="setup-two-column">
            <div className="setup-field">
              <label>
                Phone <span className="setup-optional">(Optional)</span>
              </label>
              <div className="setup-phone-row">
                <input
                  type="text"
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  placeholder="+971"
                  className="setup-phone-code"
                  maxLength="5"
                />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Phone number"
                  className="setup-phone-number"
                />
              </div>
            </div>
            <div className="setup-field">
              <label>
                Age <span className="setup-optional">(Optional)</span>
              </label>
              <input
                type="number"
                min="0"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="Add age"
              />
            </div>
          </div>

          <div className="setup-field">
            <label>
              Referral Code <span className="setup-optional">(Optional)</span>
            </label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="ABC-123"
            />
          </div>

          {error && <p className="setup-error-text">{error}</p>}

          <button type="submit" className="setup-submit-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Finish setting up profile'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SetupAccount;


